import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Filter, X, MapPin } from 'lucide-react';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon for different industry types
const getMarkerIcon = (industryType) => {
  const color = {
    'Manufacturing': '#3b82f6',
    'Technology': '#10b981',
    'Agriculture': '#f59e0b',
    'Healthcare': '#ef4444',
    'Retail': '#8b5cf6',
    'Logistics': '#06b6d4',
    'Energy': '#f97316',
    'Construction': '#78716c',
    'Textile': '#ec489a',
    'Pharmaceuticals': '#14b8a6',
    'Automotive': '#6366f1',
    'Food Processing': '#eab308'
  }[industryType] || '#6b7280';

  return L.divIcon({
    html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);"></div>`,
    className: 'custom-marker',
    iconSize: [16, 16],
    popupAnchor: [0, -8]
  });
};

// Generate 1000+ sample sites across India
const generateSites = () => {
  const indianStates = [
    { name: 'Maharashtra', districts: ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Aurangabad', 'Thane'] },
    { name: 'Karnataka', districts: ['Bengaluru', 'Mysuru', 'Hubli', 'Mangaluru', 'Belagavi', 'Kalaburagi'] },
    { name: 'Tamil Nadu', districts: ['Chennai', 'Coimbatore', 'Madurai', 'Salem', 'Tiruchirappalli', 'Tirunelveli'] },
    { name: 'Gujarat', districts: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Gandhinagar', 'Bhavnagar'] },
    { name: 'Uttar Pradesh', districts: ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Prayagraj', 'Noida'] },
    { name: 'West Bengal', districts: ['Kolkata', 'Howrah', 'Darjeeling', 'Siliguri', 'Asansol', 'Durgapur'] },
    { name: 'Telangana', districts: ['Hyderabad', 'Warangal', 'Nizamabad', 'Khammam', 'Karimnagar'] },
    { name: 'Rajasthan', districts: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner'] },
    { name: 'Delhi NCR', districts: ['New Delhi', 'Gurugram', 'Noida', 'Faridabad', 'Ghaziabad'] },
    { name: 'Punjab', districts: ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Chandigarh'] },
    { name: 'Haryana', districts: ['Gurugram', 'Faridabad', 'Panipat', 'Hisar', 'Karnal'] },
    { name: 'Andhra Pradesh', districts: ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Tirupati'] },
    { name: 'Kerala', districts: ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kollam'] },
    { name: 'Madhya Pradesh', districts: ['Bhopal', 'Indore', 'Jabalpur', 'Gwalior', 'Ujjain'] },
    { name: 'Odisha', districts: ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Puri', 'Sambalpur'] },
    { name: 'Bihar', districts: ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga'] }
  ];

  const industryTypes = [
    'Manufacturing', 'Technology', 'Agriculture', 'Healthcare', 
    'Retail', 'Logistics', 'Energy', 'Construction', 'Textile',
    'Pharmaceuticals', 'Automotive', 'Food Processing'
  ];

  const siteNames = [
    'Tech Park', 'Industrial Estate', 'SEZ', 'Manufacturing Hub', 'IT Campus',
    'Agri Processing Unit', 'Medical City', 'Retail Mall', 'Logistics Park',
    'Solar Plant', 'Construction Yard', 'Textile Mill', 'Pharma City',
    'Auto Cluster', 'Food Park', 'Business District', 'Export Zone',
    'Innovation Center', 'Research Park', 'Industrial Area'
  ];

  const sites = [];
  let id = 1;

  // Realistic coordinates for Indian cities
  const cityCoordinates = {
    'Mumbai': [19.0760, 72.8777], 'Pune': [18.5204, 73.8567], 'Nagpur': [21.1458, 79.0882],
    'Bengaluru': [12.9716, 77.5946], 'Mysuru': [12.2958, 76.6394], 'Hubli': [15.3647, 75.1239],
    'Chennai': [13.0827, 80.2707], 'Coimbatore': [11.0168, 76.9558], 'Madurai': [9.9252, 78.1198],
    'Ahmedabad': [23.0225, 72.5714], 'Surat': [21.1702, 72.8311], 'Vadodara': [22.3072, 73.1812],
    'Lucknow': [26.8467, 80.9462], 'Kanpur': [26.4499, 80.3319], 'Agra': [27.1767, 78.0081],
    'Kolkata': [22.5726, 88.3639], 'Howrah': [22.5958, 88.2636], 'Darjeeling': [27.0410, 88.2663],
    'Hyderabad': [17.3850, 78.4867], 'Warangal': [17.9689, 79.5941],
    'Jaipur': [26.9124, 75.7873], 'Jodhpur': [26.2389, 73.0243], 'Udaipur': [24.5854, 73.7125],
    'New Delhi': [28.6139, 77.2090], 'Gurugram': [28.4595, 77.0266], 'Noida': [28.5355, 77.3910],
    'Ludhiana': [30.9010, 75.8573], 'Amritsar': [31.6340, 74.8723],
    'Visakhapatnam': [17.6868, 83.2185], 'Vijayawada': [16.5062, 80.6480],
    'Thiruvananthapuram': [8.5241, 76.9366], 'Kochi': [9.9312, 76.2673],
    'Bhopal': [23.2599, 77.4126], 'Indore': [22.7196, 75.8577],
    'Bhubaneswar': [20.2961, 85.8245], 'Cuttack': [20.4625, 85.8830],
    'Patna': [25.5941, 85.1376], 'Chandigarh': [30.7333, 76.7794]
  };

  for (let i = 0; i < 1000; i++) {
    const state = indianStates[i % indianStates.length];
    const district = state.districts[i % state.districts.length];
    const industryType = industryTypes[Math.floor(Math.random() * industryTypes.length)];
    
    // Get coordinates for the district or use state center
    let lat, lng;
    if (cityCoordinates[district]) {
      lat = cityCoordinates[district][0] + (Math.random() - 0.5) * 0.3;
      lng = cityCoordinates[district][1] + (Math.random() - 0.5) * 0.3;
    } else {
      // Fallback coordinates based on state
      const stateCenters = {
        'Maharashtra': [19.5, 75.5], 'Karnataka': [15.0, 76.0], 'Tamil Nadu': [11.0, 78.0],
        'Gujarat': [23.0, 71.5], 'Uttar Pradesh': [27.0, 80.0], 'West Bengal': [23.5, 87.5],
        'Telangana': [18.0, 79.0], 'Rajasthan': [27.0, 74.0], 'Delhi NCR': [28.6, 77.2],
        'Punjab': [31.0, 75.0], 'Haryana': [29.0, 76.0], 'Andhra Pradesh': [16.0, 80.0],
        'Kerala': [10.5, 76.5], 'Madhya Pradesh': [23.5, 78.0], 'Odisha': [20.5, 85.0],
        'Bihar': [25.5, 86.0]
      };
      const center = stateCenters[state.name] || [20.5937, 78.9629];
      lat = center[0] + (Math.random() - 0.5) * 1.5;
      lng = center[1] + (Math.random() - 0.5) * 1.5;
    }
    
    sites.push({
      id: id++,
      name: `${siteNames[i % siteNames.length]} ${Math.floor(i / siteNames.length) + 1}`,
      state: state.name,
      district: district,
      industryType: industryType,
      lat: lat,
      lng: lng,
      employeeCount: Math.floor(Math.random() * 5000) + 10,
      established: 1950 + Math.floor(Math.random() * 73),
      area: Math.floor(Math.random() * 100) + 1,
      description: `Leading ${industryType.toLowerCase()} facility serving the ${district} region.`
    });
  }
  
  return sites;
};

const sitesData = generateSites();

// Extract unique values for filters
const states = [...new Set(sitesData.map(site => site.state))].sort();
const districts = [...new Set(sitesData.map(site => site.district))].sort();
const industryTypes = [...new Set(sitesData.map(site => site.industryType))].sort();

// Filter Panel Component
const FilterPanel = ({ filters, setFilters, onClose, isMobile }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  
  const handleApply = () => {
    setFilters(localFilters);
    if (isMobile) onClose();
  };
  
  const handleReset = () => {
    const resetFilters = { state: '', district: '', industryType: '' };
    setLocalFilters(resetFilters);
    setFilters(resetFilters);
    if (isMobile) onClose();
  };
  
  return (
    <div className="bg-white rounded-xl shadow-xl p-5 space-y-4">
      <div className="flex justify-between items-center border-b pb-3">
        <div className="flex items-center gap-2">
          <Filter size={20} className="text-blue-600" />
          <h3 className="font-semibold text-gray-800 text-lg">Filter Sites</h3>
        </div>
        {isMobile && (
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        )}
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
          <select
            value={localFilters.state}
            onChange={(e) => setLocalFilters({...localFilters, state: e.target.value, district: ''})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All States</option>
            {states.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
          <select
            value={localFilters.district}
            onChange={(e) => setLocalFilters({...localFilters, district: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={!localFilters.state}
          >
            <option value="">All Districts</option>
            {[...new Set(sitesData.filter(s => !localFilters.state || s.state === localFilters.state).map(s => s.district))].sort().map(district => (
              <option key={district} value={district}>{district}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Industry Type</label>
          <select
            value={localFilters.industryType}
            onChange={(e) => setLocalFilters({...localFilters, industryType: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Industries</option>
            {industryTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="flex gap-3 pt-3">
        <button
          onClick={handleApply}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Apply Filters
        </button>
        <button
          onClick={handleReset}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
        >
          Reset
        </button>
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ sites }) => {
  const stats = useMemo(() => {
    const uniqueStates = new Set(sites.map(s => s.state)).size;
    const uniqueDistricts = new Set(sites.map(s => s.district)).size;
    const industries = {};
    sites.forEach(s => {
      industries[s.industryType] = (industries[s.industryType] || 0) + 1;
    });
    const topIndustry = Object.entries(industries).sort((a,b) => b[1] - a[1])[0];
    
    return {
      total: sites.length,
      states: uniqueStates,
      districts: uniqueDistricts,
      topIndustry: topIndustry ? `${topIndustry[0]} (${topIndustry[1]})` : 'N/A'
    };
  }, [sites]);
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
        <div className="text-xs text-gray-500">Total Sites</div>
        <div className="text-2xl font-bold text-gray-800">{stats.total.toLocaleString()}</div>
      </div>
      <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
        <div className="text-xs text-gray-500">States</div>
        <div className="text-2xl font-bold text-gray-800">{stats.states}</div>
      </div>
      <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
        <div className="text-xs text-gray-500">Districts</div>
        <div className="text-2xl font-bold text-gray-800">{stats.districts}</div>
      </div>
      <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
        <div className="text-xs text-gray-500">Top Industry</div>
        <div className="text-sm font-semibold text-gray-800 truncate">{stats.topIndustry}</div>
      </div>
    </div>
  );
};

// Site List Component
const SiteList = ({ sites, onSiteSelect }) => {
  const [expandedId, setExpandedId] = useState(null);
  
  if (sites.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 text-center">
        <MapPin size={48} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500">No sites match your filters</p>
        <p className="text-sm text-gray-400 mt-1">Try adjusting your criteria</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
      {sites.map(site => (
        <div
          key={site.id}
          className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onSiteSelect(site)}
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h4 className="font-medium text-gray-800 text-sm">{site.name}</h4>
              <div className="flex flex-wrap gap-2 mt-1">
                <span className="text-xs text-gray-500">{site.state} / {site.district}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  {
                    'Manufacturing': 'bg-blue-100 text-blue-700',
                    'Technology': 'bg-green-100 text-green-700',
                    'Agriculture': 'bg-amber-100 text-amber-700',
                    'Healthcare': 'bg-red-100 text-red-700',
                    'Retail': 'bg-purple-100 text-purple-700',
                    'Logistics': 'bg-cyan-100 text-cyan-700',
                    'Energy': 'bg-orange-100 text-orange-700',
                    'Construction': 'bg-stone-100 text-stone-700',
                    'Textile': 'bg-pink-100 text-pink-700',
                    'Pharmaceuticals': 'bg-teal-100 text-teal-700',
                    'Automotive': 'bg-indigo-100 text-indigo-700',
                    'Food Processing': 'bg-yellow-100 text-yellow-700'
                  }[site.industryType] || 'bg-gray-100 text-gray-700'
                }`}>
                  {site.industryType}
                </span>
              </div>
            </div>
            <MapPin size={16} className="text-blue-500 flex-shrink-0 mt-1" />
          </div>
          {expandedId === site.id && (
            <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-600">
              <div>👥 {site.employeeCount.toLocaleString()} employees</div>
              <div>📅 Est. {site.established}</div>
              <div>📐 {site.area} acres</div>
              <div className="mt-1">{site.description}</div>
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === site.id ? null : site.id); }}
            className="text-xs text-blue-500 mt-1 hover:underline"
          >
            {expandedId === site.id ? 'Show less' : 'Show details'}
          </button>
        </div>
      ))}
    </div>
  );
};

// Main Component
const MapDashboard = () => {
  const [filters, setFilters] = useState({ state: '', district: '', industryType: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const filteredSites = useMemo(() => {
    return sitesData.filter(site => {
      if (filters.state && site.state !== filters.state) return false;
      if (filters.district && site.district !== filters.district) return false;
      if (filters.industryType && site.industryType !== filters.industryType) return false;
      return true;
    });
  }, [filters]);
  
  // Center map on India
  const mapCenter = [20.5937, 78.9629];
  const mapZoom = 5;
  
  return (
    <div className="h-screen w-full flex flex-col bg-gray-100 font-sans">
      {/* Header */}
      <header className="bg-white shadow-sm z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-1.5 rounded-lg">
              <MapPin size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">India Industrial Sites Map</h1>
              <p className="text-xs text-gray-500 hidden sm:block">{sitesData.length.toLocaleString()} industrial sites across India</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-sm text-gray-500">
              {filteredSites.length} sites shown
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Filter size={18} />
              <span className="hidden sm:inline">Filters</span>
              {(filters.state || filters.district || filters.industryType) && (
                <span className="bg-white text-blue-600 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Sidebar - Desktop */}
        <div className={`md:w-80 lg:w-96 bg-gray-50 border-r border-gray-200 flex flex-col ${showFilters && isMobile ? 'hidden' : 'flex'}`}>
          <div className="p-4 border-b border-gray-200">
            <StatsCard sites={filteredSites} />
          </div>
          
          <div className="flex-1 overflow-hidden p-4">
            <div className="mb-3 flex justify-between items-center">
              <h2 className="font-semibold text-gray-700">Industrial Sites ({filteredSites.length})</h2>
              <span className="text-xs text-gray-400">Click marker or card</span>
            </div>
            <SiteList sites={filteredSites} onSiteSelect={setSelectedSite} />
          </div>
        </div>
        
        {/* Map Container */}
        <div className="flex-1 relative">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filteredSites.map(site => (
              <Marker
                key={site.id}
                position={[site.lat, site.lng]}
                icon={getMarkerIcon(site.industryType)}
                eventHandlers={{
                  click: () => setSelectedSite(site)
                }}
              >
                <Popup>
                  <div className="p-1 min-w-[180px]">
                    <h3 className="font-bold text-gray-800">{site.name}</h3>
                    <p className="text-sm text-gray-600">{site.district}, {site.state}</p>
                    <p className="text-xs text-gray-500 mt-1">{site.industryType}</p>
                    <p className="text-xs mt-1">👥 {site.employeeCount.toLocaleString()} employees</p>
                    <p className="text-xs">📅 Established {site.established}</p>
                    <p className="text-xs">📐 {site.area} acres</p>
                    <button
                      onClick={() => setSelectedSite(site)}
                      className="mt-2 text-xs text-blue-600 hover:underline"
                    >
                      View details →
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          
          {/* Selected Site Card */}
          {selectedSite && (
            <div className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white rounded-xl shadow-xl p-4 z-[1000] border border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-gray-800">{selectedSite.name}</h3>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-gray-500">{selectedSite.district}, {selectedSite.state}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700`}>
                      {selectedSite.industryType}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedSite(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <p>👥 Employees: {selectedSite.employeeCount.toLocaleString()}</p>
                <p>📅 Established: {selectedSite.established}</p>
                <p>📐 Area: {selectedSite.area} acres</p>
                <p className="text-gray-600 text-xs mt-2">{selectedSite.description}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile Filter Modal */}
      {showFilters && isMobile && (
        <div className="fixed inset-0 bg-black/50 z-[2000] flex items-end justify-center">
          <div className="w-full max-w-md bg-white rounded-t-2xl">
            <FilterPanel
              filters={filters}
              setFilters={setFilters}
              onClose={() => setShowFilters(false)}
              isMobile={true}
            />
          </div>
        </div>
      )}
      
      {/* Desktop Filter Panel */}
      {!isMobile && showFilters && (
        <div className="fixed right-0 top-20 bottom-0 w-80 bg-white shadow-xl z-[1500] border-l border-gray-200 overflow-auto">
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            onClose={() => setShowFilters(false)}
            isMobile={false}
          />
        </div>
      )}
    </div>
  );
};

export default MapDashboard;